/* Cycling Upgrades theme behaviour. Progressive enhancement: every feature
   degrades to usable static markup without JavaScript. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Drag a scroll container with the mouse (touch already scrolls natively). A drag doesn't count
     as a click on what's under it. enabled(): whether dragging applies right now. */
  function dragScroll(el, enabled = () => true) {
    let drag = null;
    el.addEventListener('dragstart', (e) => e.preventDefault());
    el.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch' || e.button !== 0 || !enabled()) return;
      drag = { x: e.clientX, y: e.clientY, l: el.scrollLeft, t: el.scrollTop, moved: false };
    });
    el.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) > 4) { drag.moved = true; el.classList.add('is-dragging'); }
      el.scrollLeft = drag.l - dx;
      el.scrollTop = drag.t - dy;
    });
    const end = () => {
      if (drag?.moved) el.addEventListener('click', (ev) => { ev.stopImmediatePropagation(); ev.preventDefault(); }, { capture: true, once: true });
      drag = null;
      el.classList.remove('is-dragging');
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointerleave', () => { drag = null; el.classList.remove('is-dragging'); });
  }

  /* The footer strip's pixel rider (cu-trail.js CuTrail.rider) pedals across a button: in from the
     left, a no-hander hop near the end, out on the right. Resolves when it has gone. Reduced motion
     (or no cu-trail.js): resolves at once. pose: what the rider does in the air. */
  function rideAcross(el, { pose = 'nohander', duration = 1100 } = {}) {
    const rider = window.CuTrail && window.CuTrail.rider;
    if (reduceMotion || !rider) return Promise.resolve();
    const W = el.clientWidth, H = el.clientHeight;
    const dpr = Math.max(1, Math.round(window.devicePixelRatio || 1));
    const canvas = document.createElement('canvas');
    canvas.className = 'cu-ride__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    el.classList.add('cu-ride', 'is-riding');
    el.append(canvas);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = false;
    const dust = [];
    return new Promise((resolve) => {
      const t0 = performance.now();
      const frame = (now) => {
        const u = Math.min(1, (now - t0) / duration);
        const x = -44 + (W + 52) * u;
        const hop = (u - 0.6) / 0.28;
        const air = hop > 0 && hop < 1;
        const lift = air ? Math.sin(Math.PI * hop) * Math.min(16, H * 0.3) : 0;
        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = '#beb69e'; // dashed trail line, scrolling under the rider
        for (let gx = -((x * 0.5) % 8); gx < W; gx += 8) ctx.fillRect(Math.round(gx), H - 4, 4, 1);
        if (!air) dust.push({ x: x + 4 + Math.random() * 4, y: H - 5, life: 1 });
        ctx.fillStyle = '#8c8573';
        dust.forEach((d) => {
          d.life -= 0.05; d.y -= 0.35; d.x -= 0.6;
          ctx.globalAlpha = Math.max(0, d.life) * 0.6;
          ctx.fillRect(Math.round(d.x), Math.round(d.y), 2, 2);
        });
        ctx.globalAlpha = 1;
        ctx.drawImage(rider(x * 1.6, air, air ? pose : 'ride'), Math.round(x), Math.round(H - 35 - lift));
        if (u < 1) requestAnimationFrame(frame);
        else { canvas.remove(); resolve(); }
      };
      requestAnimationFrame(frame);
    });
  }
  // Coming back to a page from the bfcache: no button is mid-ride.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) document.querySelectorAll('.cu-ride.is-riding').forEach((el) => el.classList.remove('is-riding'));
  });

  /* The header and menu-drawer cart links (snippets/cu-cart-count). bump: a little wiggle. */
  function setCartCount(n, bump = false) {
    document.querySelectorAll('[data-cu-cart-n]').forEach((el) => { el.textContent = n; });
    document.querySelectorAll('[data-cu-cart-sr]').forEach((el) => { el.textContent = `Cart, ${n} ${n === 1 ? 'item' : 'items'}`; });
    if (!bump || reduceMotion) return;
    document.querySelectorAll('.cu-cart-link').forEach((a) => {
      a.classList.remove('is-bump');
      void a.offsetWidth;
      a.classList.add('is-bump');
      a.addEventListener('animationend', () => a.classList.remove('is-bump'), { once: true });
    });
  }

  /* Show one item of a set and keep ARIA + inertness in sync. */
  function activate(items, index, attr = 'is-active') {
    items.forEach((el, i) => {
      const on = i === index;
      el.classList.toggle(attr, on);
      if (el.hasAttribute('data-cu-hide-inactive')) {
        el.setAttribute('aria-hidden', String(!on));
        el.inert = !on;
      }
    });
  }

  /* ---------- Reveal: content fades up as it enters the viewport (CSS: Load + scroll motion) ----------
     Each section is walked: WRAP elements are layout shells, walked into; SPLIT containers reveal their
     children one by one (staggered); anything else is one block. Heroes with their own motion are skipped. */
  (() => {
    const html = document.documentElement;
    const ready = () => html.classList.remove('cu-reveal-pending');
    if (reduceMotion || !('IntersectionObserver' in window)) return ready();
    const SKIP = 'script, style, template, noscript, dialog, canvas, .cu-visually-hidden, .cu-vhero, .cu-xhero, .cu-hero, .cu-trail';
    const WRAP = '.cu-container, .cu-section, .cu-pagehero, .cu-pdp, .cu-specifics, .cu-footer';
    const SPLIT = '.cu-pagehero__copy, .cu-grid, .cu-colls, .cu-cards, .cu-az, .cu-acc, .cu-specifics > dl, .cu-footer__top';
    const units = [];
    const walk = (el) => {
      [...el.children].forEach((c) => {
        if (c.matches(SKIP)) return;
        if (c.matches(SPLIT)) units.push(...[...c.children].filter((g) => !g.matches(SKIP)));
        else if (c.matches(WRAP)) walk(c);
        else units.push(c);
      });
    };
    document.querySelectorAll('#MainContent > .shopify-section, .shopify-section-group-footer-group').forEach(walk);
    units.forEach((u) => u.classList.add('cu-rv'));
    ready();
    const start = performance.now();
    // Blocks arriving together stagger in page order; the first screenful waits a beat for the header.
    const showAll = (list) => {
      const base = performance.now() - start < 500 ? 0.3 : 0;
      list.forEach((u, i) => {
        io.unobserve(u);
        u.style.setProperty('--cu-rv-delay', `${base + Math.min(i, 8) * 0.08}s`);
        u.classList.add('is-in');
        const clean = (ev) => {
          if (ev.target !== u || ev.propertyName !== 'transform') return;
          u.classList.remove('cu-rv', 'is-in');
          u.style.removeProperty('--cu-rv-delay');
          u.removeEventListener('transitionend', clean);
        };
        u.addEventListener('transitionend', clean);
      });
    };
    const io = new IntersectionObserver((entries) => {
      showAll(entries.filter((e) => e.isIntersecting).map((e) => e.target));
    }, { rootMargin: '0px 0px -6% 0px' });
    units.forEach((u) => io.observe(u));
    // The -6% margin never reaches blocks in the last strip of the page (the footer's bottom row),
    // so at the end of the page everything still waiting on screen comes in.
    const atEnd = () => {
      if (innerHeight + scrollY < document.documentElement.scrollHeight - 2) return;
      showAll(units.filter((u) => u.classList.contains('cu-rv') && !u.classList.contains('is-in') && u.getBoundingClientRect().top < innerHeight));
    };
    addEventListener('scroll', atEnd, { passive: true });
    atEnd();
  })();

  /* ---------- Hero carousel (WAI-ARIA carousel pattern) ---------- */
  document.querySelectorAll('[data-cu-hero]').forEach((hero) => {
    const copies = [...hero.querySelectorAll('[data-cu-slide-copy]')];
    const images = [...hero.querySelectorAll('[data-cu-slide-img]')];
    const thumbs = [...hero.querySelectorAll('[data-cu-slide-thumb]')];
    const pause = hero.querySelector('[data-cu-pause]');
    const live = hero.querySelector('[data-cu-live]');
    if (copies.length < 2) return;
    let index = 0;
    let timer = null;
    let userPaused = reduceMotion;

    const go = (i, announce) => {
      index = (i + copies.length) % copies.length;
      activate(copies, index);
      activate(images, index);
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === index)));
      // Keep the current thumbnail in view without scrolling the page.
      const t = thumbs[index];
      if (strip && t) {
        const l = t.offsetLeft, pad = 8; // strip is the offsetParent
        if (l < strip.scrollLeft + pad) strip.scrollTo({ left: l - pad, behavior: reduceMotion ? 'auto' : 'smooth' });
        else if (l + t.offsetWidth > strip.scrollLeft + strip.clientWidth - pad) strip.scrollTo({ left: l + t.offsetWidth - strip.clientWidth + pad, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      if (announce && live) live.textContent = `Slide ${index + 1} of ${copies.length}`;
    };
    const stop = () => { clearInterval(timer); timer = null; };
    const start = () => {
      if (userPaused || timer) return;
      timer = setInterval(() => go(index + 1, false), 6000);
    };
    const syncPause = () => {
      if (!pause) return;
      pause.setAttribute('aria-pressed', String(userPaused));
      pause.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
      pause.querySelector('[data-icon-pause]').hidden = userPaused;
      pause.querySelector('[data-icon-play]').hidden = !userPaused;
      if (live) live.setAttribute('aria-live', userPaused ? 'polite' : 'off');
    };

    thumbs.forEach((t, n) => t.addEventListener('click', () => { go(n, true); userPaused = true; stop(); syncPause(); }));
    pause?.addEventListener('click', () => { userPaused = !userPaused; userPaused ? stop() : start(); syncPause(); });
    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);
    hero.addEventListener('focusin', stop);
    hero.addEventListener('focusout', (e) => { if (!hero.contains(e.relatedTarget)) start(); });

    go(0, false);
    syncPause();
    start();
  });

  /* ---------- Muted background video: autoplay, pausable via a [data-cu-video-toggle]
     button (WCAG 2.2.2), never autoplays for prefers-reduced-motion. Off-screen videos
     are paused to save battery; the user's choice wins. ---------- */
  function bindVideos(videos, btn) {
    if (!videos.length) return;
    let userPaused = reduceMotion;
    const label = btn?.dataset.label || 'video';
    const sync = () => {
      if (!btn) return;
      btn.setAttribute('aria-pressed', String(userPaused));
      btn.setAttribute('aria-label', `${userPaused ? 'Play' : 'Pause'} ${label}`);
      btn.querySelector('[data-icon-pause]').hidden = userPaused;
      btn.querySelector('[data-icon-play]').hidden = !userPaused;
    };
    const visible = new Set();
    const update = (v) => { (!userPaused && visible.has(v)) ? v.play().catch(() => {}) : v.pause(); };
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      e.isIntersecting ? visible.add(e.target) : visible.delete(e.target);
      update(e.target);
    }));
    videos.forEach((v) => {
      v.muted = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('aria-hidden', 'true');
      v.removeAttribute('autoplay');
      if (!reduceMotion) io.observe(v);
    });
    btn?.addEventListener('click', () => { userPaused = !userPaused; videos.forEach(update); sync(); });
    sync();
  }

  document.querySelectorAll('[data-cu-vhero]').forEach((hero) => {
    if (hero.querySelector('[data-cu-video-toggle]')) bindVideos([...hero.querySelectorAll('video')], hero.querySelector('[data-cu-video-toggle]'));
  });
  /* ---------- Motion scenes (assets/cu-motion.js): draw(t) on a canvas sized to its box. Plays
     once when scrolled into view (about 4s, so no pause control is needed under WCAG 2.2.2) and
     holds the finished frame; reduced motion shows the finished frame straight away. ---------- */
  document.querySelectorAll('canvas[data-cu-motion]').forEach((canvas) => {
    const scene = window.CuMotion?.scenes[canvas.dataset.cuMotion];
    if (!scene) return;
    const ctx = canvas.getContext('2d');
    let W = 0, H = 0, dpr = 1, t = reduceMotion ? scene.duration : 0;
    const draw = () => {
      if (!W || !H) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      scene.draw(ctx, Math.min(t, scene.duration), W, H);
    };
    new ResizeObserver(() => {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      draw();
    }).observe(canvas);
    document.fonts?.ready.then(draw);
    if (reduceMotion) return;
    let start = 0;
    const tick = (now) => {
      if (!start) start = now;
      t = (now - start) / 1000;
      draw();
      if (t < scene.duration) requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(canvas);
  });

  /* ---------- Ride strip (assets/cu-trail.js): trail or road scene, picked by the section setting
     or at random per page load. Endless, so it gets the same video-like play()/pause() and
     bindVideos handles the pause toggle, off-screen pausing and the reduced-motion still.
     Tapping the strip queues a backflip (trail) or a sprint (road) while it's playing. ---------- */
  document.querySelectorAll('canvas[data-cu-trail]').forEach((canvas) => {
    if (!window.CuTrail) return;
    const names = Object.keys(window.CuTrail.scenes);
    const pick = canvas.dataset.cuTrail;
    const name = names.includes(pick) ? pick : names[Math.floor(Math.random() * names.length)]; // "random": one per page load
    const scene = window.CuTrail.scenes[name];
    const hit = canvas.closest('[data-cu-trail-boost]');
    hit?.setAttribute('aria-label', name === 'road' ? 'Make the rider sprint' : 'Make the rider jump');
    const ctx = canvas.getContext('2d');
    const boosts = [];
    let W = 0, H = 0, dpr = 1, t = scene.still, raf = 0, last = 0;
    const draw = () => {
      if (!W || !H) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      scene.draw(ctx, t, W, H, boosts);
    };
    new ResizeObserver(() => {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      draw();
    }).observe(canvas);
    const tick = (now) => { if (last) t += Math.min(0.1, (now - last) / 1000); last = now; draw(); raf = requestAnimationFrame(tick); };
    canvas.play = () => { if (!raf) { last = 0; raf = requestAnimationFrame(tick); } return Promise.resolve(); };
    canvas.pause = () => { cancelAnimationFrame(raf); raf = 0; };
    hit?.addEventListener('click', () => {
      if (!raf) return;
      if (boosts.length && t - boosts[boosts.length - 1] < 2) return; // one flip at a time
      boosts.push(t);
      if (boosts.length > 3) boosts.shift();
    });
    bindVideos([canvas], canvas.closest('section')?.querySelector('[data-cu-video-toggle]'));
  });

  document.querySelectorAll('[data-cu-videos]').forEach((root) => {
    const videos = [...root.querySelectorAll('video')];
    root.classList.toggle('has-motion', !reduceMotion && videos.length > 0);
    bindVideos(videos, root.querySelector('[data-cu-video-toggle]'));
  });

  /* ---------- Exploded bike: scroll position through the section drives the explode;
     reduced motion shows it exploded and still. ---------- */
  document.querySelectorAll('[data-cu-xhero]').forEach((hero) => {
    const parts = [...hero.querySelectorAll('.cu-bk-part')].map((el) => ({
      el, x: +el.dataset.x, y: +el.dataset.y, r: +el.dataset.r, d: +el.dataset.d,
      tags: (el.dataset.tags || '').split(' ').filter(Boolean),
      chain: el.dataset.cuPart === 'chain',
    }));
    const labels = hero.querySelectorAll('[data-part]');
    const clamp = (v) => Math.min(1, Math.max(0, v));
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const apply = (e) => {
      parts.forEach((p) => {
        if (p.chain) { p.el.style.opacity = String(1 - clamp(e / 0.2)); return; }
        const t = ease(clamp((e - p.d) / 0.7));
        p.el.style.transform = `translate(${(p.x * t).toFixed(1)}px, ${(p.y * t).toFixed(1)}px) rotate(${(p.r * t).toFixed(2)}deg)`;
      });
      const settle = ease(clamp(e / 0.8));
      hero.style.setProperty('--co', (1 - clamp(e / 0.3)).toFixed(3));
      hero.style.setProperty('--xs', (0.8 + 0.2 * settle).toFixed(4));
      hero.style.setProperty('--xo', (1 - settle).toFixed(4));
      const lo = clamp((e - 0.55) / 0.2);
      hero.style.setProperty('--lo', lo.toFixed(3));
      hero.classList.toggle('is-open', lo > 0.3); // clickable as soon as they're visible
    };

    // Hovering or focusing a label lights up its part(s).
    labels.forEach((a) => {
      const hot = (on) => parts.forEach((p) => { if (p.tags.includes(a.dataset.part)) p.el.classList.toggle('is-hot', on); });
      a.addEventListener('mouseenter', () => hot(true));
      a.addEventListener('mouseleave', () => hot(false));
      a.addEventListener('focus', () => hot(true));
      a.addEventListener('blur', () => hot(false));
    });

    if (reduceMotion) { hero.classList.add('is-static'); apply(1); return; }
    hero.classList.add('is-live');
    // Lines draw themselves in the first time the section comes into view.
    const DRAW_DELAY = { frame: 100, 'rear-wheel': 250, 'front-wheel': 250, fork: 400, crankset: 400, seat: 550, cockpit: 550 };
    const draw = () => {
      parts.forEach((p) => {
        const delay = DRAW_DELAY[p.el.dataset.cuPart] ?? 700;
        p.el.querySelectorAll('[pathLength]').forEach((el) => el.animate(
          [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }],
          { duration: 1600, delay, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'forwards' },
        ));
      });
      hero.querySelectorAll('.cu-bk-teeth, .cu-bk-chain, .cu-bk-decal, .cu-bk-rim').forEach((el) => {
        el.animate([{ opacity: 0 }, { opacity: getComputedStyle(el).getPropertyValue('--o-end') || 1 }],
          { duration: 800, delay: 1100, easing: 'ease', fill: 'forwards' });
      });
    };
    new IntersectionObserver(([entry], io) => {
      if (entry.isIntersecting) { draw(); io.disconnect(); }
    }, { threshold: 0.35 }).observe(hero.querySelector('.cu-xhero__stage'));

    let ticking = false;
    const range = () => hero.offsetHeight - hero.querySelector('.cu-xhero__stage').offsetHeight;
    const stickTop = () => parseFloat(getComputedStyle(hero).getPropertyValue('--xh-top')) || 0;
    const update = () => {
      ticking = false;
      const p = clamp((stickTop() - hero.getBoundingClientRect().top) / range());
      apply(clamp(p / 0.85)); // explode over the first 85% of the scroll, then a short hold
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    window.addEventListener('resize', update);
    update();

    // Keyboard users tabbing into the labels jump to the exploded view so focus is visible.
    hero.addEventListener('focusin', (e) => {
      if (!e.target.closest('[data-part]') || hero.classList.contains('is-open')) return;
      const y = hero.getBoundingClientRect().top + window.scrollY - stickTop() + range() * 0.7;
      window.scrollTo({ top: y, behavior: 'instant' });
    });
  });

  /* ---------- Home: bike hero. Each bike MP4 holds the bike (top half) and its alpha matte
     (bottom half); WebGL combines them so the bike sits in front of the tagline. The intro
     plays once, then the loop takes over from the same frame. Pausable (WCAG 2.2.2), paused
     off-screen; reduced motion and no-WebGL keep the still image. ---------- */
  document.querySelectorAll('[data-cu-bhero]').forEach((hero) => {
    const canvas = hero.querySelector('[data-cu-bhero-canvas]');
    const still = hero.querySelector('[data-cu-bhero-still]');
    const btn = hero.querySelector('[data-cu-bhero-pause]');
    if (reduceMotion || !hero.dataset.intro) return;

    // Depth: tagline and bike drift a few px in opposite directions with the pointer.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      let raf = null;
      hero.addEventListener('pointermove', (e) => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const r = hero.getBoundingClientRect();
          hero.style.setProperty('--px', (((e.clientX - r.left) / r.width) * 2 - 1).toFixed(3));
          hero.style.setProperty('--py', (((e.clientY - r.top) / r.height) * 2 - 1).toFixed(3));
        });
      });
      hero.addEventListener('pointerleave', () => { hero.style.setProperty('--px', 0); hero.style.setProperty('--py', 0); });
    }

    const gl = canvas.getContext('webgl', { premultipliedAlpha: true, alpha: true });
    if (!gl) return;

    const makeVideo = (src, loop) => {
      const v = document.createElement('video');
      Object.assign(v, { muted: true, loop, playsInline: true, preload: 'auto', crossOrigin: 'anonymous', src });
      v.setAttribute('playsinline', '');
      return v;
    };
    const intro = makeVideo(hero.dataset.intro, false);
    const loopVideo = hero.dataset.loop ? makeVideo(hero.dataset.loop, true) : null;
    let video = intro;

    const shader = (type, src) => { const sh = gl.createShader(type); gl.shaderSource(sh, src); gl.compileShader(sh); return sh; };
    const prog = gl.createProgram();
    gl.attachShader(prog, shader(gl.VERTEX_SHADER, 'attribute vec2 p; varying vec2 uv; void main(){ uv = vec2(p.x * .5 + .5, .5 - p.y * .5); gl_Position = vec4(p, 0., 1.); }'));
    gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, 'precision mediump float; varying vec2 uv; uniform sampler2D t; void main(){ vec3 c = texture2D(t, vec2(uv.x, uv.y * .5)).rgb; float a = texture2D(t, vec2(uv.x, .5 + uv.y * .5)).r; gl_FragColor = vec4(c * a, a); }'));
    gl.linkProgram(prog);
    gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
    [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T].forEach((k) => gl.texParameteri(gl.TEXTURE_2D, k, gl.CLAMP_TO_EDGE));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

    let shown = false;
    const draw = () => {
      if (video.readyState < 2) return;
      if (canvas.width !== video.videoWidth) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight / 2;
        gl.viewport(0, 0, canvas.width, canvas.height);
      }
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, video);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      if (!shown) { shown = true; canvas.hidden = false; still.classList.add('is-covered'); }
    };
    const frame = (v) => () => {
      if (v !== video) return;
      draw();
      if (!v.paused && !v.ended) (v.requestVideoFrameCallback ? v.requestVideoFrameCallback(frame(v)) : requestAnimationFrame(frame(v)));
    };
    [intro, loopVideo].filter(Boolean).forEach((v) => v.addEventListener('play', frame(v)));
    intro.addEventListener('loadeddata', draw);
    // The intro ends on the loop's first frame: keep that frame on screen and start the loop.
    intro.addEventListener('ended', () => {
      if (!loopVideo) return;
      video = loopVideo;
      sync();
    });

    let userPaused = false;
    let visible = true;
    const sync = () => {
      const playing = !userPaused;
      btn.setAttribute('aria-pressed', String(!playing));
      btn.setAttribute('aria-label', playing ? 'Pause animation' : 'Play animation');
      btn.querySelector('[data-icon-pause]').hidden = !playing;
      btn.querySelector('[data-icon-play]').hidden = playing;
      if (playing && visible && !document.hidden && !(video === intro && intro.ended)) video.play().catch(() => {});
      else video.pause();
    };
    btn.hidden = false;
    btn.addEventListener('click', () => { userPaused = !userPaused; sync(); });
    new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; sync(); }).observe(hero);
    document.addEventListener('visibilitychange', sync);
    sync();
  });

  /* ---------- Floating Menu button: slides in once the header (on the home page, the hero
     under its transparent header) has scrolled away ---------- */
  document.querySelectorAll('[data-cu-menu-float]').forEach((btn) => {
    const header = btn.closest('.cu-header');
    const until = (header.classList.contains('cu-header--overlay') && document.querySelector('[data-cu-vhero]')) || header;
    const show = (on) => { btn.classList.toggle('is-shown', on); btn.inert = !on; };
    show(false);
    new IntersectionObserver(([entry]) => show(!entry.isIntersecting)).observe(until);
  });

  /* ---------- Hover/focus switchers: cluster links and brand list ---------- */
  document.querySelectorAll('[data-cu-switch]').forEach((root) => {
    const triggers = [...root.querySelectorAll('[data-cu-switch-trigger]')];
    const panes = [...root.querySelectorAll('[data-cu-switch-pane]')];
    const set = (i) => { activate(triggers, i); activate(panes, i); };
    triggers.forEach((t, i) => {
      const target = t.matches('a, button') ? t : t.querySelector('a, button') || t;
      target.addEventListener('mouseenter', () => set(i));
      target.addEventListener('focus', () => set(i));
    });
  });

  /* ---------- Brand reel: plays once (3.5s, under the WCAG 2.2.2 five-second limit) when
     scrolled into view, then reveals the live links on its last frame. Reduced motion, phones,
     keyboard focus, blocked autoplay and errors all go straight to the links. ---------- */
  document.querySelectorAll('[data-cu-reel]').forEach((reel) => {
    const video = reel.querySelector('video');
    const done = () => reel.classList.add('is-done');
    if (!video || reduceMotion || window.matchMedia('(max-width: 749px)').matches) return done();
    video.addEventListener('ended', done);
    video.addEventListener('error', done, true);
    reel.addEventListener('focusin', done);
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      video.play().catch(done);
    }, { threshold: 0.5 });
    io.observe(reel);
  });

  /* ---------- Product gallery ---------- */
  document.querySelectorAll('[data-cu-gallery]').forEach((g) => {
    const slides = [...g.querySelectorAll('[data-cu-gallery-slide]')];
    const thumbs = [...g.querySelectorAll('[data-cu-gallery-thumb]')];
    const strip = g.querySelector('[data-cu-gallery-strip]');
    if (strip) dragScroll(strip);
    const count = g.querySelector('[data-cu-gallery-count]');
    const live = g.querySelector('[data-cu-live]');
    if (slides.length < 2) return;
    let index = 0;
    const pad = (n) => String(n).padStart(2, '0');
    const go = (i) => {
      index = (i + slides.length) % slides.length;
      activate(slides, index);
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === index)));
      if (count) count.textContent = `${pad(index + 1)} / ${pad(slides.length)}`;
      if (live) live.textContent = `Image ${index + 1} of ${slides.length}`;
    };
    g.querySelector('[data-cu-gallery-prev]')?.addEventListener('click', () => go(index - 1));
    g.querySelector('[data-cu-gallery-next]')?.addEventListener('click', () => go(index + 1));
    thumbs.forEach((t, n) => t.addEventListener('click', () => go(n)));
    g.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { go(index - 1); e.preventDefault(); }
      if (e.key === 'ArrowRight') { go(index + 1); e.preventDefault(); }
    });
  });

  /* ---------- Add to cart without leaving the page ----------
     The rider rides the button while /cart/add.js runs, the photo flies to the cart link, then the
     button reads "Added". One-of-one parts swap the buy row for Checkout / View cart. Without JS the
     form posts to the cart as usual. */
  const bindAtc = (form) => {
    const btn = form.querySelector('[data-cu-atc-btn]');
    const label = form.querySelector('[data-cu-atc-label]');
    const actions = form.querySelector('[data-cu-atc-actions]');
    const done = form.querySelector('[data-cu-atc-done]');
    const status = form.querySelector('[data-cu-atc-status]');
    const error = form.querySelector('[data-cu-atc-error]');
    if (!btn || !label || !window.fetch) return;
    const single = form.dataset.cuAtc === 'true';
    const idle = label.innerHTML;
    const tick = done?.querySelector('svg')?.outerHTML || '';
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    let busy = false;

    // The current photo arcs up and shrinks into whichever cart link is on screen.
    const fly = () => {
      const img = document.querySelector('.cu-gallery__slide.is-active img');
      const target = [...document.querySelectorAll('.cu-cart-link, [data-cu-menu-float]')]
        .map((el) => el.getBoundingClientRect())
        .find((r) => r.width && r.bottom > 0 && r.top < innerHeight);
      if (reduceMotion || !img || !target) return;
      const from = img.getBoundingClientRect();
      const ghost = img.cloneNode();
      ghost.removeAttribute('srcset');
      ghost.src = img.currentSrc || img.src;
      ghost.alt = '';
      ghost.className = 'cu-atc-fly';
      Object.assign(ghost.style, { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px` });
      document.body.append(ghost);
      const dx = target.left + target.width / 2 - (from.left + from.width / 2);
      const dy = target.top + target.height / 2 - (from.top + from.height / 2);
      const s = 28 / from.width;
      ghost.animate([
        { transform: 'translate(0, 0) scale(1) rotate(0)', opacity: 1 },
        { transform: `translate(${dx * 0.25}px, ${dy * 0.25 - 120}px) scale(0.45) rotate(-10deg)`, opacity: 1, offset: 0.4 },
        { transform: `translate(${dx}px, ${dy}px) scale(${s}) rotate(14deg)`, opacity: 0.4 },
      ], { duration: 850, easing: 'cubic-bezier(0.5, 0, 0.75, 0)' }).finished.then(() => ghost.remove(), () => ghost.remove());
    };

    form.addEventListener('submit', async (e) => {
      if (e.submitter && e.submitter !== btn) return;
      e.preventDefault();
      if (busy || btn.disabled) return;
      busy = true;
      btn.setAttribute('aria-disabled', 'true');
      if (error) error.hidden = true;
      if (status) status.textContent = 'Adding to cart';
      btn.classList.remove('is-added');
      const add = fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
        .then(async (r) => {
          const body = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(body.description || body.message || 'Sorry, that couldn’t be added to your cart.');
          return body;
        });
      const ride = rideAcross(btn);
      try {
        await add;
        await ride;
        btn.classList.remove('is-riding');
        btn.classList.add('cu-ride', 'is-added');
        label.innerHTML = `${tick}Added`;
        fly();
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
        setTimeout(() => setCartCount(cart ? cart.item_count : 0, true), reduceMotion ? 0 : 750);
        if (status) status.textContent = 'Added to cart';
        if (done) {
          setTimeout(() => {
            if (single && actions) actions.hidden = true;
            done.hidden = false;
            done.classList.add('is-new');
            if (single) done.querySelector('a')?.focus({ preventScroll: true });
          }, reduceMotion ? 0 : 1100);
        }
        if (!single) setTimeout(() => { btn.classList.remove('is-added'); label.innerHTML = idle; }, 2400);
      } catch (err) {
        await ride;
        btn.classList.remove('is-riding', 'is-added');
        label.innerHTML = idle;
        if (status) status.textContent = '';
        if (error) { error.textContent = err.message; error.hidden = false; }
      } finally {
        btn.removeAttribute('aria-disabled');
        busy = false;
      }
    });
  };
  document.querySelectorAll('form[data-cu-atc]').forEach(bindAtc);

  /* ---------- Checkout buttons: the rider sprints across as the checkout loads ---------- */
  const bindRide = (el) => {
    el.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      rideAcross(el, { pose: el.dataset.cuRide || 'nohander', duration: 800 });
    });
  };
  document.querySelectorAll('[data-cu-ride]').forEach(bindRide);

  /* ---------- Variant pills: re-render the variant-dependent parts of the buy column ----------
     Price, SKU, stock, the add-to-cart form and pickup all come back from the section's own Liquid
     (Section Rendering API), so there is one template for every variant. */
  let variantController;
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cu-variant-id]');
    const picker = input && input.closest('[data-cu-variant-picker]');
    const buy = picker && picker.closest('.cu-buy');
    if (!buy || !input.dataset.cuVariantId) return;
    const id = input.dataset.cuVariantId;
    buy.querySelector('form input[name="id"]').value = id;
    variantController?.abort();
    const mine = variantController = new AbortController();
    buy.setAttribute('data-cu-variant-busy', '');
    try {
      const url = new URL(location.href);
      url.searchParams.set('variant', id);
      history.replaceState(history.state, '', url);
      url.searchParams.set('section_id', picker.dataset.cuVariantPicker);
      const res = await fetch(url, { signal: mine.signal });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      buy.querySelectorAll('[data-cu-variant-part]').forEach((part) => {
        const fresh = doc.querySelector(`[data-cu-variant-part="${part.dataset.cuVariantPart}"]`);
        if (!fresh) return;
        part.replaceWith(fresh);
        fresh.querySelectorAll('form[data-cu-atc]').forEach(bindAtc);
        fresh.querySelectorAll('[data-cu-ride]').forEach(bindRide);
      });
      document.getElementById(input.id)?.focus({ preventScroll: true });
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn('Variant refresh failed, reloading', err);
      location.assign(`${location.pathname}?variant=${id}`);
    } finally {
      if (variantController === mine) buy.removeAttribute('data-cu-variant-busy');
    }
  });

  /* ---------- Product photo lightbox (native <dialog>: focus trap, Esc, focus return) ---------- */
  document.querySelectorAll('[data-cu-lightbox]').forEach((dialog) => {
    const gallery = dialog.closest('[data-cu-gallery]');
    const openers = [...gallery.querySelectorAll('[data-cu-lightbox-open]')];
    const img = dialog.querySelector('[data-cu-lightbox-img]');
    const stage = dialog.querySelector('[data-cu-lightbox-stage]');
    const count = dialog.querySelector('[data-cu-lightbox-count]');
    let index = 0;
    let returnFocus = null;
    const status = dialog.querySelector('[data-cu-lightbox-status]');
    const preloaded = new Set();
    const preload = (i) => {
      const o = openers[(i + openers.length) % openers.length];
      if (!o || preloaded.has(o.dataset.full)) return;
      preloaded.add(o.dataset.full);
      const pre = new Image();
      pre.src = o.dataset.full;
    };
    // Fit every photo to the available space (upscaling small uploads) so all
    // photos show at the same large size regardless of their resolution.
    // Zoom is 2.5x the fitted size (or the photo's full resolution if that's larger).
    const fit = () => {
      if (!img.naturalWidth) return;
      const cs = getComputedStyle(stage);
      const availW = stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const availH = stage.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      let scale = Math.min(availW / img.naturalWidth, availH / img.naturalHeight);
      if (stage.classList.contains('is-zoomed')) scale = Math.max(scale * 2.5, 1);
      img.style.width = `${Math.floor(img.naturalWidth * scale)}px`;
      img.style.height = `${Math.floor(img.naturalHeight * scale)}px`;
    };
    window.addEventListener('resize', () => { if (dialog.open) fit(); });
    const loaded = () => {
      fit();
      stage.classList.remove('is-loading');
      if (status) status.textContent = '';
    };
    img.addEventListener('load', loaded);
    img.addEventListener('error', loaded);
    const show = (i) => {
      index = (i + openers.length) % openers.length;
      const o = openers[index];
      stage.classList.remove('is-zoomed');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      fit();
      if (img.getAttribute('src') !== o.dataset.full) {
        stage.classList.add('is-loading');
        if (status) status.textContent = 'Loading photo';
        img.src = o.dataset.full;
        if (img.complete && img.naturalWidth) loaded();
      }
      img.alt = o.dataset.alt || '';
      if (count) count.textContent = `Photo ${index + 1} of ${openers.length}`;
      preload(index + 1);
      preload(index - 1);
    };
    const toggleZoom = (e) => {
      // Remember which point of the photo was clicked (0–1), zoom, then scroll so
      // that point sits in the centre of the viewer.
      const before = img.getBoundingClientRect();
      const fx = e ? (e.clientX - before.left) / before.width : 0.5;
      const fy = e ? (e.clientY - before.top) / before.height : 0.5;
      const zoomed = stage.classList.toggle('is-zoomed');
      fit();
      if (zoomed) {
        const after = img.getBoundingClientRect();
        const stageBox = stage.getBoundingClientRect();
        stage.scrollLeft += (after.left + fx * after.width) - (stageBox.left + stage.clientWidth / 2);
        stage.scrollTop += (after.top + fy * after.height) - (stageBox.top + stage.clientHeight / 2);
      } else {
        stage.scrollLeft = 0;
        stage.scrollTop = 0;
      }
    };
    openers.forEach((o, i) => o.addEventListener('click', () => {
      returnFocus = o;
      show(i);
      dialog.showModal();
      fit();
      dialog.querySelector('[data-cu-lightbox-close]').focus();
    }));
    img.addEventListener('click', toggleZoom);
    dialog.querySelector('[data-cu-lightbox-close]').addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-cu-lightbox-prev]')?.addEventListener('click', () => show(index - 1));
    dialog.querySelector('[data-cu-lightbox-next]')?.addEventListener('click', () => show(index + 1));
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { show(index - 1); e.preventDefault(); }
      if (e.key === 'ArrowRight') { show(index + 1); e.preventDefault(); }
    });
    dialog.addEventListener('close', () => { stage.classList.remove('is-zoomed'); returnFocus?.focus(); });
    // Drag to pan while zoomed.
    dragScroll(stage, () => stage.classList.contains('is-zoomed'));
  });

  /* ---------- Drawer drill-down navigation ----------
     Each sub-menu swipes in as its own screen. On wide screens a .cu-drawer--cascade drawer
     instead opens each level as a new column beside its parent and grows to fit (--cu-cols). */
  document.querySelectorAll('[data-cu-drill]').forEach((drill) => {
    const panels = [...drill.querySelectorAll('[data-cu-drill-panel]')];
    const root = panels[0];
    const dialog = drill.closest('dialog');
    const wide = dialog?.classList.contains('cu-drawer--cascade') ? window.matchMedia('(min-width: 900px)') : null;
    const cascading = () => Boolean(wide?.matches);
    // Open panels, root first; the last is the current screen (or the deepest column).
    const trail = [root];
    const current = () => trail[trail.length - 1];
    const parentOf = (p) => document.getElementById(p.querySelector('[data-cu-drill-back]')?.dataset.cuDrillBack);
    const openerOf = (p) => parentOf(p)?.querySelector(`[data-cu-drill-open="${p.id}"]`);
    const titleOf = (p) => p.querySelector('.cu-drill__title') || p;
    const setCols = () => dialog?.style.setProperty('--cu-cols', cascading() ? trail.length : 1);

    // One screen at a time: slide the next panel over the last.
    const swap = (from, to, dir, focusEl) => {
      to.hidden = false;
      to.classList.add('is-active');
      from.classList.remove('is-active');
      if (!reduceMotion) {
        to.classList.add(dir === 'fwd' ? 'is-in-fwd' : 'is-in-back');
        from.classList.add(dir === 'fwd' ? 'is-out-fwd' : 'is-out-back');
        const done = () => {
          to.classList.remove('is-in-fwd', 'is-in-back');
          from.classList.remove('is-out-fwd', 'is-out-back');
          from.hidden = true;
        };
        to.addEventListener('animationend', done, { once: true });
      } else {
        from.hidden = true;
      }
      to.scrollTop = 0;
      (focusEl || titleOf(to)).focus({ preventScroll: true });
    };

    // Columns: close everything deeper than `panel`.
    const closeTo = (panel) => {
      while (trail.length > 1 && current() !== panel) {
        const p = trail.pop();
        p.hidden = true;
        p.classList.remove('is-active', 'is-reveal');
        openerOf(p)?.setAttribute('aria-expanded', 'false');
      }
      setCols();
    };
    const reveal = (to) => {
      closeTo(parentOf(to));
      openerOf(to)?.setAttribute('aria-expanded', 'true');
      to.hidden = false;
      to.classList.add('is-active');
      if (!reduceMotion) {
        to.classList.add('is-reveal');
        const done = (e) => { if (e.target !== to) return; to.classList.remove('is-reveal'); to.removeEventListener('animationend', done); };
        to.addEventListener('animationend', done);
      }
      to.scrollTop = 0;
      trail.push(to);
      setCols();
      titleOf(to).focus({ preventScroll: true });
    };

    drill.querySelectorAll('[data-cu-drill-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = document.getElementById(btn.dataset.cuDrillOpen);
        if (cascading()) {
          // A second click on an open item folds its column away.
          if (trail.includes(target)) { closeTo(parentOf(target)); btn.focus(); } else reveal(target);
          return;
        }
        btn.setAttribute('aria-expanded', 'true');
        const from = current();
        trail.push(target);
        swap(from, target, 'fwd', target.querySelector('.cu-drill__back'));
      });
    });
    const back = () => {
      if (trail.length < 2) return;
      const from = current();
      const opener = openerOf(from);
      if (cascading()) { closeTo(parentOf(from)); opener?.focus(); return; }
      trail.pop();
      opener?.setAttribute('aria-expanded', 'false');
      swap(from, current(), 'back', opener);
    };
    drill.querySelectorAll('[data-cu-drill-back]').forEach((b) => b.addEventListener('click', back));

    // Esc steps back a level before it closes the drawer (registered before the drawer's own Esc handler).
    dialog?.addEventListener('cancel', (e) => {
      if (trail.length < 2) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      back();
    });
    // Back to the top level whenever the drawer closes or the layout switches.
    const reset = () => {
      panels.forEach((p) => {
        p.classList.remove('is-active', 'is-reveal', 'is-in-fwd', 'is-in-back', 'is-out-fwd', 'is-out-back');
        p.hidden = p !== root;
      });
      root.classList.add('is-active');
      drill.querySelectorAll('[data-cu-drill-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      trail.length = 1;
      setCols();
    };
    dialog?.addEventListener('close', reset);
    wide?.addEventListener('change', reset);
  });

  /* ---------- Menu drawer (native <dialog>: focus trap + Esc built in) ----------
     A dialog can have several openers (the home page's hero and floating Menu buttons);
     focus goes back to whichever one opened it. */
  const dialogOpeners = new Map();
  document.querySelectorAll('[data-cu-dialog-open]').forEach((btn) => {
    const dialog = document.getElementById(btn.getAttribute('aria-controls'));
    if (!dialog || typeof dialog.showModal !== 'function') return;
    if (!dialogOpeners.has(dialog)) dialogOpeners.set(dialog, []);
    dialogOpeners.get(dialog).push(btn);
  });
  dialogOpeners.forEach((openers, dialog) => {
    let opener = null;
    // Animate out before closing (skipped for reduced motion).
    const closeAnimated = () => {
      if (reduceMotion || dialog.classList.contains('is-closing')) { dialog.close(); return; }
      dialog.classList.add('is-closing');
      dialog.addEventListener('animationend', () => {
        dialog.classList.remove('is-closing');
        dialog.close();
      }, { once: true });
    };
    openers.forEach((btn) => btn.addEventListener('click', () => {
      opener = btn;
      dialog.showModal();
      btn.setAttribute('aria-expanded', 'true');
    }));
    dialog.addEventListener('close', () => {
      opener?.setAttribute('aria-expanded', 'false');
      opener?.focus();
    });
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); closeAnimated(); });
    dialog.querySelectorAll('[data-cu-dialog-close]').forEach((c) => c.addEventListener('click', closeAnimated));
    dialog.addEventListener('click', (e) => { if (e.target === dialog) closeAnimated(); });
  });

  /* ---------- Spotlight search (combobox + listbox over the Predictive Search API) ---------- */
  const spot = document.querySelector('[data-cu-spotlight]');
  if (spot && typeof spot.showModal === 'function') {
    const input = spot.querySelector('[data-cu-spotlight-input]');
    const list = spot.querySelector('[data-cu-spotlight-list]');
    const quick = spot.querySelector('[data-cu-spotlight-quick]');
    const status = spot.querySelector('[data-cu-spotlight-status]');
    const recentWrap = spot.querySelector('[data-cu-spotlight-recent]');
    const recentList = spot.querySelector('[data-cu-spotlight-recent-list]');
    const triggers = [...document.querySelectorAll('[data-cu-spotlight-open]')];
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency });
    const cache = new Map();
    const RECENT_KEY = 'cu-recent-searches';
    // Full results page (sections/main-search.liquid) for when the suggestions aren't it.
    const searchUrl = (q) => `${root}search?${new URLSearchParams({ q, type: 'product' })}`;
    let options = [];
    let active = -1;
    let timer = null;
    let controller = null;
    let opener = null;
    const panel = spot.querySelector('.cu-spotlight__panel');

    // Vertically centred until there are results, then pinned near the top like Spotlight.
    const place = () => {
      const top = spot.classList.contains('has-results')
        ? window.innerHeight * 0.12
        : Math.max(16, (window.innerHeight - panel.offsetHeight) / 2);
      spot.style.setProperty('--cu-spot-top', `${Math.round(top)}px`);
    };
    new ResizeObserver(() => { if (spot.open) place(); }).observe(panel);
    window.addEventListener('resize', () => { if (spot.open) place(); });

    if (!/Mac|iPhone|iPad/.test(navigator.platform)) {
      document.querySelectorAll('[data-cu-kbd]').forEach((k) => { k.textContent = 'Ctrl K'; });
    }

    const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; } };
    const saveRecent = (q) => {
      q = q.trim();
      if (!q) return;
      const next = [q, ...readRecent().filter((r) => r.toLowerCase() !== q.toLowerCase())].slice(0, 5);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch (e) { /* storage unavailable */ }
    };
    const renderRecent = () => {
      const recent = readRecent();
      recentWrap.hidden = !recent.length;
      recentList.replaceChildren(...recent.map((q) => {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cu-spotlight__row';
        b.innerHTML = '<span class="cu-spotlight__icon" aria-hidden="true">↺</span><span class="cu-spotlight__title"></span>';
        b.lastChild.textContent = q;
        b.addEventListener('click', () => { input.value = q; search(); input.focus(); });
        li.append(b);
        return li;
      }));
    };

    const setActive = (i, scroll = true) => {
      options.forEach((o, n) => o.setAttribute('aria-selected', String(n === i)));
      active = i;
      if (i < 0) { input.removeAttribute('aria-activedescendant'); return; }
      input.setAttribute('aria-activedescendant', options[i].id);
      if (scroll) options[i].scrollIntoView({ block: 'nearest' });
    };

    const option = (href, title, meta, thumb, extra, modifier) => {
      const a = document.createElement('a');
      a.href = href;
      a.id = `CuSpotOpt-${options.length}`;
      a.className = modifier ? `cu-spotlight__row ${modifier}` : 'cu-spotlight__row';
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      a.tabIndex = -1;
      const media = document.createElement('span');
      media.className = thumb ? 'cu-spotlight__thumb' : 'cu-spotlight__icon';
      media.setAttribute('aria-hidden', 'true');
      if (thumb) {
        const img = document.createElement('img');
        img.src = thumb; img.alt = ''; img.width = 96; img.height = 96; img.loading = 'lazy';
        media.append(img);
      } else {
        media.textContent = '→';
      }
      const text = document.createElement('span');
      text.className = 'cu-spotlight__text';
      const t = document.createElement('span');
      t.className = 'cu-spotlight__title';
      t.textContent = title;
      text.append(t);
      if (meta) {
        const m = document.createElement('span');
        m.className = 'cu-spotlight__meta';
        m.textContent = meta;
        text.append(m);
      }
      a.append(media, text);
      if (extra) {
        const x = document.createElement('span');
        x.className = 'cu-spotlight__price';
        x.textContent = extra;
        a.append(x);
      }
      const n = options.length;
      a.addEventListener('mousemove', () => { if (active !== n) setActive(n, false); });
      a.addEventListener('click', () => saveRecent(input.value));
      options.push(a);
      return a;
    };

    const group = (label, items) => {
      if (!items.length) return null;
      const g = document.createElement('div');
      g.setAttribute('role', 'group');
      g.className = 'cu-spotlight__group';
      const id = `CuSpotGrp-${label.replace(/\W/g, '')}`;
      g.setAttribute('aria-labelledby', id);
      const h = document.createElement('p');
      h.id = id;
      h.className = 'cu-spotlight__heading cu-label';
      h.textContent = label;
      g.append(h, ...items);
      return g;
    };

    // Alert bar under the search box: "Get an email when new … parts land" saves the keyword
    // search to My garage (assets/cu-garage.js); once saved it links to My garage instead.
    const alertBar = spot.querySelector('[data-cu-spotlight-alertbar]');
    const alertText = spot.querySelector('[data-cu-spotlight-alerttext]');
    const alertBtn = spot.querySelector('[data-cu-spotlight-alertbtn]');
    const alertLink = spot.querySelector('[data-cu-spotlight-alertlink]');
    let alertQ = '';
    const paintAlert = (q, note) => {
      const G = window.CuGarage;
      alertQ = q;
      if (!alertBar || !G || !G.saveSearchAlert || !q) { if (alertBar) alertBar.hidden = true; return; }
      const on = G.isSearchSaved(q);
      alertBar.hidden = false;
      alertBar.classList.toggle('is-on', on);
      alertText.textContent = note || (on ? `Alerts are on for “${q}”.` : `Get an email when new “${q}” parts land.`);
      alertBtn.hidden = on;
      alertLink.hidden = !on;
    };
    alertBtn?.addEventListener('click', async () => {
      if (!alertQ || alertBtn.getAttribute('aria-disabled') === 'true') return;
      const q = alertQ;
      alertBtn.setAttribute('aria-disabled', 'true');
      const r = await window.CuGarage.saveSearchAlert(q);
      alertBtn.removeAttribute('aria-disabled');
      if (r.redirecting || q !== alertQ) return;
      paintAlert(q, r.ok ? null : r.message);
    });

    const render = (q, r) => {
      options = [];
      paintAlert(q);
      // Suggestions print above the products (Korby, 2026-10-15). Built first too, so the
      // options array (arrow-key order) matches the order on screen.
      const queries = (r.queries || []).filter((s) => s.text.toLowerCase() !== q.toLowerCase())
        .map((s) => option(s.url, s.text, 'Suggestion'));
      const products = (r.products || []).map((p) => {
        // Same title-prefix grade rule as snippets/cu-condition.liquid ("Light Use! SRAM …").
        const bang = p.title.indexOf('!');
        const grade = bang > 0 ? p.title.slice(0, bang).trim() : '';
        const graded = grade.length > 1 && grade.length <= 24;
        const meta = [p.vendor, graded ? grade : null, p.available ? null : 'Sold'].filter(Boolean).join(' · ');
        const img = p.featured_image && p.featured_image.url
          ? `${p.featured_image.url}${p.featured_image.url.includes('?') ? '&' : '?'}width=96` : null;
        return option(p.url, p.title, meta, img, money.format(parseFloat(p.price)));
      });
      const collections = (r.collections || []).map((c) => option(c.url, c.title, 'Collection'));
      const pages = (r.pages || []).map((p) => option(p.url, p.title, 'Page'));
      const groups = [
        group('Suggestions', queries),
        group('Products', products),
        group('Categories', collections),
        group('Pages', pages),
      ].filter(Boolean);

      const all = option(searchUrl(q), `See all results for “${q}”`, null, null, null, 'cu-spotlight__all');
      const tail = [all];
      if (!groups.length) {
        const empty = document.createElement('p');
        empty.className = 'cu-spotlight__empty';
        empty.textContent = `No matches for “${q}”. Try a brand, part or speed.`;
        list.replaceChildren(empty, ...tail);
      } else {
        list.replaceChildren(...groups, ...tail);
      }
      quick.hidden = true;
      spot.classList.add('has-results');
      place();
      input.setAttribute('aria-expanded', 'true');
      // Nothing is picked until the arrows move: Enter on a typed query opens all results,
      // not the first product (Korby, 2026-10-15).
      setActive(-1);
      const found = options.length - 1; // the last option is "See all results"
      status.textContent = found
        ? `${found} results. Use up and down arrows to review, Enter to see all results.`
        : `No matches for ${q}. Press Enter to search all products.`;
    };

    const reset = () => {
      controller?.abort();
      options = [];
      list.replaceChildren();
      paintAlert('');
      quick.hidden = false;
      spot.classList.remove('is-loading', 'has-results');
      place();
      input.setAttribute('aria-expanded', 'false');
      setActive(-1);
      status.textContent = '';
      renderRecent();
    };

    const search = async () => {
      const q = input.value.trim();
      if (!q) { reset(); return; }
      if (cache.has(q)) { render(q, cache.get(q)); return; }
      controller?.abort();
      controller = new AbortController();
      spot.classList.add('is-loading');
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,query,page',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
      });
      try {
        const res = await fetch(`${root}search/suggest.json?${params}`, { signal: controller.signal });
        const data = await res.json();
        const r = (data.resources && data.resources.results) || {};
        cache.set(q, r);
        if (input.value.trim() === q) render(q, r);
      } catch (e) {
        if (e.name !== 'AbortError') status.textContent = 'Search is unavailable right now.';
      } finally {
        spot.classList.remove('is-loading');
      }
    };

    const open = (q = '') => {
      if (spot.open) { input.focus(); return; }
      opener = document.activeElement;
      spot.showModal();
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      input.value = q;
      spot.classList.add('is-placing');
      q ? search() : reset();
      place();
      requestAnimationFrame(() => requestAnimationFrame(() => spot.classList.remove('is-placing')));
      input.focus();
      input.select();
    };
    const close = () => {
      if (reduceMotion || spot.classList.contains('is-closing')) { spot.close(); return; }
      spot.classList.add('is-closing');
      spot.addEventListener('animationend', () => { spot.classList.remove('is-closing'); spot.close(); }, { once: true });
    };

    // A trigger inside another dialog (the menu drawer) closes it first; once it has handed
    // focus back to its own opener, Spotlight opens and later returns focus there.
    triggers.forEach((t) => t.addEventListener('click', () => {
      const host = t.closest('dialog[open]');
      if (!host) { open(); return; }
      host.addEventListener('close', () => open(), { once: true });
      host.close();
    }));
    spot.querySelector('[data-cu-spotlight-close]').addEventListener('click', close);
    spot.addEventListener('click', (e) => { if (e.target === spot) close(); });
    spot.addEventListener('cancel', (e) => {
      e.preventDefault();
      // Like Spotlight: the first Esc clears the query, the second closes.
      if (input.value) { input.value = ''; reset(); } else close();
    });
    spot.addEventListener('close', () => {
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      (opener && document.contains(opener) ? opener : triggers[0])?.focus();
    });

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(search, 160);
    });
    input.addEventListener('keydown', (e) => {
      if (!options.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((active + 1) % options.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active < 0 ? options.length - 1 : (active - 1 + options.length) % options.length); }
      else if (e.key === 'Home' && e.ctrlKey) { e.preventDefault(); setActive(0); }
      else if (e.key === 'End' && e.ctrlKey) { e.preventDefault(); setActive(options.length - 1); }
    });
    spot.querySelector('[data-cu-spotlight-form]').addEventListener('submit', (e) => {
      e.preventDefault();
      const q = input.value.trim();
      if (!q) return;
      saveRecent(q);
      // An option picked with the arrows opens; otherwise Enter goes to the full results.
      const target = options[active];
      window.location.href = target ? target.href : searchUrl(q);
    });

    document.addEventListener('keydown', (e) => {
      const typing = e.target.closest('input, textarea, select, [contenteditable="true"]');
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); open(); }
      else if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); open(); }
    });
  }

  /* ---------- Cookie banner (snippets/cu-consent.liquid) on Shopify's Customer Privacy API.
     Shown only when Shopify says this visitor must be asked; the choice goes back to Shopify,
     which stores it in its consent cookie (so it is remembered) and applies it to pixels and apps. */
  const consent = document.querySelector('[data-cu-consent]');
  if (consent && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      const privacy = window.Shopify.customerPrivacy;
      if (error || !privacy || !privacy.shouldShowBanner()) return;
      // Shopify's own banner (Settings → Customer privacy) wins if it's turned on.
      if (document.getElementById('shopify-pc__banner')) return;
      consent.hidden = false;
      consent.querySelectorAll('[data-cu-consent-choice]').forEach((btn) => btn.addEventListener('click', () => {
        const ok = btn.dataset.cuConsentChoice === 'accept';
        consent.querySelectorAll('button').forEach((b) => { b.disabled = true; });
        privacy.setTrackingConsent({ analytics: ok, marketing: ok, preferences: ok, sale_of_data: ok }, () => {
          consent.hidden = true;
        });
      }));
    });
  }

  /* ---------- Same-day shipping cutoff (Mountain Time) ---------- */
  const cutoff = parseInt(document.body.dataset.cutoffHour || '12', 10);
  const shipEls = document.querySelectorAll('[data-cu-ship]');
  if (shipEls.length) {
    const update = () => {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Denver', hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
      }).formatToParts(new Date());
      const get = (t) => (parts.find((p) => p.type === t) || {}).value;
      const h = parseInt(get('hour'), 10) % 24;
      const m = parseInt(get('minute'), 10);
      const weekend = ['Sat', 'Sun'].includes(get('weekday'));
      const left = cutoff * 60 - (h * 60 + m);
      const open = !weekend && left > 0;
      const label = cutoff > 12 ? `${cutoff - 12}pm` : cutoff === 12 ? '12pm' : `${cutoff}am`;
      shipEls.forEach((el) => {
        const mode = el.dataset.cuShip;
        if (mode === 'bar') {
          const fill = el.querySelector('.cu-shipbar__fill');
          const text = el.querySelector('[data-cu-ship-text]');
          if (text) text.textContent = open ? `Order in ${Math.floor(left / 60)}h ${left % 60}m — ships today` : 'Ships next business day';
          if (fill) fill.style.width = open ? `${Math.max(4, Math.round((1 - left / (cutoff * 60)) * 100))}%` : '100%';
        } else {
          el.textContent = open ? `Order before ${label} MT and it ships today.` : 'Ships next business day.';
        }
      });
    };
    update();
    setInterval(update, 60000);
  }

  /* ---------- Filter drawer (snippets/cu-facets.liquid) ----------
     Opening, drill-down and Esc come from the drawer + drill code above. This adds the
     per-group summaries, "Search brand…" narrowing, a live "Show N results" count and instant sort. */
  document.querySelectorAll('[data-cu-facets]').forEach((form) => {
    const show = form.querySelector('[data-cu-facets-show]');
    const groups = [...form.querySelectorAll('[data-cu-facet-group]')];
    const currency = form.dataset.currency || '';
    let timer = null;
    let controller = null;

    const summarize = () => groups.forEach((g) => {
      const out = form.querySelector(`[data-cu-facet-picked="${g.id}"]`);
      const [lo, hi] = [...g.querySelectorAll('input[type="number"]')].map((n) => n.value.trim());
      const picked = [...g.querySelectorAll('[data-cu-facet-opt] input:checked')]
        .map((i) => i.closest('[data-cu-facet-opt]').querySelector('.cu-facets__label').textContent.trim());
      if (lo || hi) out.textContent = `${currency}${lo || 0}–${hi ? currency + hi : 'max'}`;
      else out.textContent = picked.length > 1 ? `${picked.length} selected` : picked[0] || '';
    });

    // Re-render this section with the pending filters and copy back the count + per-value counts.
    const recount = async () => {
      controller?.abort();
      controller = new AbortController();
      const params = new URLSearchParams([...new FormData(form)].filter(([, v]) => v !== ''));
      params.set('section_id', form.dataset.section);
      form.classList.add('is-counting');
      try {
        const res = await fetch(`${form.getAttribute('action')}?${params}`, { signal: controller.signal });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('[data-cu-facets-show]');
        if (fresh) show.textContent = fresh.textContent;
        form.querySelectorAll('[data-cu-facet-opt] input').forEach((input) => {
          const match = doc.querySelector(`[data-cu-facet-opt] input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`);
          const count = input.closest('[data-cu-facet-opt]').querySelector('[data-cu-facet-count]');
          count.textContent = match ? match.closest('[data-cu-facet-opt]').querySelector('[data-cu-facet-count]').textContent : '0';
          input.disabled = match ? match.disabled : !input.checked;
        });
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('Filter count unavailable', e);
      } finally {
        form.classList.remove('is-counting');
      }
    };
    const changed = () => {
      summarize();
      clearTimeout(timer);
      timer = setTimeout(recount, 200);
    };

    form.addEventListener('change', (e) => { if (!e.target.matches('[data-cu-facet-find], [data-cu-sort]')) changed(); });

    // Sorting applies straight away, keeping the applied filters (not any unsaved drawer picks).
    form.querySelector('[data-cu-sort]')?.addEventListener('change', (e) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      url.searchParams.delete('page');
      window.location.href = url;
    });

    form.querySelectorAll('[data-cu-facet-find]').forEach((find) => {
      const group = find.closest('[data-cu-facet-group]');
      const opts = [...group.querySelectorAll('[data-cu-facet-opt]')];
      const none = group.querySelector('[data-cu-facet-none]');
      find.addEventListener('input', () => {
        const q = find.value.trim().toLowerCase();
        let shown = 0;
        opts.forEach((o) => {
          const hit = !q || o.querySelector('.cu-facets__label').textContent.toLowerCase().includes(q);
          o.hidden = !hit;
          shown += hit;
        });
        none.hidden = shown > 0;
      });
      find.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
        // Like Spotlight: Esc clears the search first, then steps back.
        if (e.key === 'Escape' && find.value) {
          e.preventDefault();
          e.stopPropagation();
          find.value = '';
          find.dispatchEvent(new Event('input'));
        }
      });
    });

    // "Clear all" in the drawer unticks everything in place; without JS it's a link.
    form.querySelector('[data-cu-facets-clear]')?.addEventListener('click', (e) => {
      e.preventDefault();
      form.querySelectorAll('[data-cu-facet-opt] input:checked').forEach((i) => { i.checked = false; });
      groups.forEach((g) => g.querySelectorAll('input[type="number"]').forEach((n) => { n.value = ''; }));
      changed();
    });

    summarize();
  });

  /* ---------- Brand directory (Shop New): category pills, cards and search filter the tiles ---------- */
  document.querySelectorAll('[data-cu-dir]').forEach((dir) => {
    const bar = dir.querySelector('[data-cu-dir-bar]');
    const items = [...dir.querySelectorAll('[data-cu-dir-item]')];
    const pills = [...dir.querySelectorAll('[data-cu-dir-cat]')];
    const q = dir.querySelector('[data-cu-dir-q]');
    const status = dir.querySelector('[data-cu-dir-status]');
    const empty = dir.querySelector('[data-cu-dir-empty]');
    if (!bar) return;
    bar.hidden = false;
    let cat = '';
    const apply = () => {
      const needle = q.value.trim().toLowerCase();
      let shown = 0;
      items.forEach((li) => {
        const on = (!cat || li.dataset.cats.split(' ').includes(cat)) && (!needle || li.dataset.name.includes(needle));
        li.hidden = !on;
        if (on) shown += 1;
      });
      pills.forEach((p) => p.setAttribute('aria-pressed', String(p.dataset.cuDirCat === cat)));
      // A–Z list: hide letters left empty, and their jump links.
      dir.querySelectorAll('[data-cu-dir-group]').forEach((g) => {
        g.hidden = !g.querySelector('[data-cu-dir-item]:not([hidden])');
        const jump = dir.querySelector(`[data-cu-dir-jump="${g.dataset.cuDirGroup}"]`);
        if (jump) jump.hidden = g.hidden;
      });
      status.textContent = `${shown} ${shown === 1 ? 'brand' : 'brands'}`;
      empty.hidden = shown > 0;
    };
    pills.forEach((p) => p.addEventListener('click', () => { cat = p.dataset.cuDirCat; apply(); }));
    q.addEventListener('input', apply);
    // Category cards jump to the directory (their href) already filtered.
    dir.querySelectorAll('[data-cu-dir-pick]').forEach((a) => a.addEventListener('click', () => {
      cat = a.dataset.cuDirPick;
      q.value = '';
      apply();
    }));
  });

  /* ---------- Product recommendations (section rendering API) ---------- */
  // Product suggestion rows (sections/product-recommendations.liquid): try each URL
  // until one returns products, drop the product being viewed (and, for "you might
  // also need", its type), cap at the row's limit,
  // and only then un-hide the row, so an empty row never leaves a gap.
  document.querySelectorAll('[data-cu-recs][data-urls]').forEach(async (el) => {
    const limit = Number(el.dataset.limit) || 4;
    for (const url of el.dataset.urls.split(' ').filter(Boolean)) {
      try {
        const html = await (await fetch(url)).text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cu-recs]');
        if (!fresh) continue;
        fresh.querySelectorAll('[data-handle]').forEach((li) => {
          if (li.dataset.handle === el.dataset.exclude || (el.dataset.excludeType && li.dataset.type === el.dataset.excludeType)) li.remove();
        });
        const items = [...fresh.querySelectorAll('[data-handle]')];
        if (!items.length) continue;
        items.slice(limit).forEach((li) => li.remove());
        el.innerHTML = fresh.innerHTML;
        if (el.dataset.heading) el.querySelector('h2').textContent = el.dataset.heading;
        el.hidden = false;
        return;
      } catch (e) {
        /* Suggestions are optional; try the next source, else stay hidden. */
      }
    }
  });

  // Shopify Forms (the 5% off pop-up) renders in an open shadow root the theme CSS
  // can't reach; swap its heavy 80px shadow for the theme's floating-pill shadow, and lift
  // the minimised tab 100px above its right-middle default, clear of the chat panel.
  // Its class names are hashed per build, so match on the stable prefix.
  const FORMS_CSS = '[class*="_teaserContainer_"] { box-shadow: var(--cu-float-shadow) !important; }'
    + ' #app-embed[data-current-step="tease"] { top: calc(50% - 100px) !important; }';
  const styleForms = (embed) => {
    const root = embed.shadowRoot;
    if (!root || root.querySelector('style[data-cu]')) return !!root;
    const style = document.createElement('style');
    style.dataset.cu = '';
    style.textContent = FORMS_CSS;
    root.append(style);
    return true;
  };
  let formsTries = 0;
  const formsTimer = setInterval(() => {
    const embed = document.querySelector('shopify-forms-embed');
    if ((embed && styleForms(embed)) || ++formsTries > 40) clearInterval(formsTimer);
  }, 250);
})();
